import { Injectable } from '@angular/core';

import {
  AgricolaBalanceValidatePayload,
  AgricolaAssistancePlansPayload,
  AgricolaClientAccountsPayload,
  AgricolaClientInfoPayload,
  AgricolaCreatePolicyPayload,
  AgricolaSellerValidatePayload
} from '../interfaces/agricola.interface';
import { RequestParams } from './request-params';
import { RequestService } from './request.service';

export interface ApoloContactSearchPayload {
  country_id: number;
  country_sap_code: string;
  sponsor_id: string;
  value: string;
  type_value: number;
  page: number;
  per_page: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(private readonly request: RequestService) {}

  // ---------------------------------------------------------------------------
  // System
  // ---------------------------------------------------------------------------

  system = {
    health: (): RequestParams =>
      this.request.get().url('/api/health'),
  };

  // ---------------------------------------------------------------------------
  // User
  // ---------------------------------------------------------------------------

  me = {
    current: (): RequestParams =>
      this.request.get().url('/api/me').auth(),
  };

  // ---------------------------------------------------------------------------
  // Agricola
  // ---------------------------------------------------------------------------

  agricola = {
    products: (): RequestParams =>
      this.request.get().url('/api/ext/agricola/products').auth(),

    plans: (payload: { internal_product_code: string }): RequestParams =>
      this.request.post().url('/api/ext/agricola/plans').payload(payload).auth(),

    assistancePlans: (payload: AgricolaAssistancePlansPayload): RequestParams =>
      this.request.post().url('/api/ext/agricola/plans/assistance').payload(payload).auth(),

    getAssistanceAddOns: (payload: { plan: number | string }): RequestParams =>
      this.request.post().url('/api/ext/agricola/plans/addon/search').payload(payload).auth(),

    validateAssistanceProduct: (payload: { internal_product_code: string }): RequestParams =>
      this.request.post().url('/api/agricola/validateAssistanceProduct').payload(payload).auth(),

    createAssistanceSale: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/create').payload(payload).auth(),

    prevalidateAssistanceSale: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/prevalidate').payload(payload).auth(),

    createAssistancePrivateLink: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/private-link').payload(payload).auth(),

    getAssistancePrivateLink: (token: string): RequestParams =>
      this.request.get().url('/api/ext/agricola/assistance/private-link/{}', token).auth(),

    updateAssistancePrivateLink: (token: string, payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/private-link/{}/update', token).payload(payload).auth(),

    emitAssistancePrivateLink: (token: string, payload: any = {}): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/private-link/{}/emit', token).payload(payload).auth(),

    rejectAssistancePrivateLink: (token: string, payload: any = {}): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/private-link/{}/reject', token).payload(payload).auth(),

    sendAssistancePrivateLinkEmail: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/private-link/email').payload(payload).auth(),

    sendAssistancePrivateLinkSms: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/private-link/sms').payload(payload).auth(),

    countries: (): RequestParams =>
      this.request.get().url('/api/ext/agricola/countries').auth(),

    states: (countryId: string | number): RequestParams =>
      this.request.get().url('/api/ext/agricola/states/{}', countryId).auth(),

    substates: (countryId: string | number, stateId: string | number): RequestParams =>
      this.request.get().url('/api/ext/agricola/substates/{}/{}', countryId, stateId).auth(),

    cities: (countryId: string | number, substateId: string | number): RequestParams =>
      this.request.get().url('/api/ext/agricola/cities/{}/{}', countryId, substateId).auth(),

    identificationTypes: (countryId: string | number): RequestParams =>
      this.request.get().url('/api/ext/agricola/identification-types/{}', countryId).auth(),

    genders: (): RequestParams =>
      this.request.get().url('/api/ext/agricola/genders').auth(),

    agencies: (): RequestParams =>
      this.request.get().url('/api/ext/agricola/agencies').auth(),

    locationsByProduct: (product: string): RequestParams =>
      this.request.get().url('/api/ext/agricola/locations/product/{}', product).auth(),

    searchClient: (payload: AgricolaClientInfoPayload): RequestParams =>
      this.request.post().url('/api/ext/agricola/clients/info').payload(payload).auth(),

    getClientAccounts: (payload: AgricolaClientAccountsPayload): RequestParams =>
      this.request.post().url('/api/ext/agricola/clients/accounts').payload(payload).auth(),

    validateBalance: (payload: AgricolaBalanceValidatePayload): RequestParams =>
      this.request.post().url('/api/ext/agricola/balance/validate').payload(payload).auth(),

    validateSeller: (payload: AgricolaSellerValidatePayload): RequestParams =>
      this.request.post().url('/api/ext/agricola/sellers/validate').payload(payload).auth(),

    issuePolicy: (payload: AgricolaCreatePolicyPayload): RequestParams =>
      this.request.post().url('/api/ext/agricola/policies').payload(payload).auth(),

    createInsuranceSale: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/insurance/sales').payload(payload).auth(),

    createTmkInsurancePending: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/telemarketing/insurance/pending').payload(payload).auth(),

    createTmkAssistancePending: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/telemarketing/assistance/pending').payload(payload).auth(),

    // No token in the URL: the BFF does not validate a ?token= here and the
    // core is now reached by the BFF with a Bearer service token, so the user's
    // MSAL token no longer needs to travel in the query string. window.open
    // still works because the endpoint requires no query token.
    policyDocumentUrl: (policyNumber: string): string =>
      `/api/document_without_signature_auth/${encodeURIComponent(policyNumber)}`,

    downloadPolicyDocument: (policyNumber: string): RequestParams =>
      this.request.get().url('/api/document_without_signature_auth/{}', policyNumber)
        .responseType('blob').observeResponse(),

    resendPolicy: (policyNumber: string): RequestParams =>
      this.request.post().url('/api/ext/agricola/policies/{}/resend', policyNumber).payload({}).auth(),

    // --- Sell report (Reporte de ventas) ---

    reports: (filter: Record<string, unknown>, page: number, perPage = 50): RequestParams =>
      this.request.get().url('/api/ext/agricola/reports')
        .page(page).per_page(perPage).filter(filter).auth(),

    exportReport: (filter: Record<string, unknown>): RequestParams =>
      this.request.get().url('/api/ext/agricola/reports/export')
        .filter(filter).auth(),

    reportsList: (filter: Record<string, unknown>, page: number, perPage = 50): RequestParams =>
      this.request.get().url('/api/ext/agricola/reports/list')
        .page(page).per_page(perPage).filter(filter).auth(),

    // Feature 1: read-only audit of the customer's signature (client_sign_emission_log)
    // for the seller review screen.
    signEmissionLog: (emissionLogId: number): RequestParams =>
      this.request.get().url('/api/ext/agricola/assistance/sign-emission-log/{}', emissionLogId).auth(),

    // Feature 2: per-row companion for the sell report — signature/certificate ids
    // per policy. Never 404s (has_signature=false when there is no evidence).
    policyDocuments: (policyNumber: string): RequestParams =>
      this.request.get().url('/api/ext/agricola/assistance/policy-documents/{}', policyNumber).auth(),

    attachmentDownloadUrl: (attachmentId: string | number, mode = 1): string =>
      `/api/ext/agricola/attachments/${attachmentId}/download/${mode}`,

    downloadAttachment: (attachmentId: string | number, mode = 1): RequestParams =>
      this.request.get().url('/api/ext/agricola/attachments/{}/download/{}', attachmentId, mode)
        .responseType('blob').observeResponse().auth(),

    // Proxies the physical-signature ("contingencia") template through our API so
    // the browser never sees the raw GCS URL. Authenticated, so it must be fetched
    // as a blob (a plain navigation can't send the bearer token).
    downloadContingencyTemplate: (): RequestParams =>
      this.request.get().url('/api/ext/agricola/contingency-template')
        .responseType('blob').observeResponse().auth(),

    // Proxies the "condicionado" (policy wording) PDF through our API so the
    // browser never sees the raw storage URL. Fetched as a blob for the same reason.
    // The wording is resolved upstream from the selected plan's config, falling
    // back to the product's own config, so the plan id travels when there is one.
    downloadCondicionado: (internalProductCode: string, planId?: number | string): RequestParams =>
      this.request.get().url('/api/ext/agricola/condicionado')
        .params({
          internal_product_code: internalProductCode,
          ...(planId ? { plan_id: planId } : {})
        })
        .responseType('blob').observeResponse().auth(),

    // Assistance contingency: multipart uploadFile (contingency PDF) +
    // uploadData (assistance sale JSON). Persisted upstream via contingency-sign.
    createAssistanceSaleContingency: (formData: FormData): RequestParams =>
      this.request.post().url('/api/ext/agricola/assistance/contingency-sign').file(formData).auth(),


    searchContactApolo: (payload: ApoloContactSearchPayload): RequestParams =>
      this.request.post().url('/api/ext/agricola/getucontactdataapolo').auth().payload(payload),


    assistanceById: (assistanceId: string): RequestParams =>
      this.request.get().url('/api/ext/agricola/get_assistance_sale_by_id/{}', assistanceId).auth(),


    chubbTransaction: (payload: any): RequestParams =>
      this.request.post().url('/api/ext/agricola/chubb_transaction').auth().payload(payload),

    getChubbTransactions: (params: any): RequestParams =>
      this.request.get().url('/api/ext/agricola/chubb_transaction').auth().params(params),

    getAssistanceSaleExtraData: (assistanceSaleId: number): RequestParams =>
      this.request.get().url('/api/ext/agricola/assistance/sales/{}/extra-data', assistanceSaleId).auth(),

    getAssistanceSaleExtraInfo: (assistanceSaleId: number): RequestParams =>
      this.request.get().url('/api/ext/agricola/assistance/sales/{}/extra-info', assistanceSaleId).auth(),


    getAssistancePaymentRequirement: (filter: object): RequestParams =>
      this.request.get().url('/api/ext/agricola/assistance-payment-requirement').auth().params({ filter: JSON.stringify(filter) }),

    getCatalogEquivalences: (params?: any): RequestParams =>
      this.request.get().url('/api/ext/agricola/catalog_equivalences').auth().params(params),




    // --- Public customer signature page (no .auth(): the customer has no session;
    // the private-link token is the capability). The apiserver proxies to S2 and
    // sets the Referer server-side for @private_link. ---
    getPublicAssistanceLink: (token: string): RequestParams =>
      this.request.get().url('/api/public/assistance/private-link/{}', token),

    savePublicAssistanceSignature: (token: string, formData: FormData): RequestParams =>
      this.request.post().url('/api/public/assistance/private-link/{}/save-signature', token).file(formData),

    publicAssistanceDocumentUrl: (policyNumber: string, transactionNumber: string): string =>
      `/api/public/assistance/document/${policyNumber}/${transactionNumber}`,

    downloadPublicAssistanceDocument: (policyNumber: string, transactionNumber: string): RequestParams =>
      this.request.get().url('/api/public/assistance/document/{}/{}', policyNumber, transactionNumber)
        .responseType('blob').observeResponse(),
  };

  relationships = {
    global: (): RequestParams =>
      this.request.get().url('/api/ext/agricola/relationship'),

    productSpecific: (productCode: string): RequestParams =>
      this.request.get().url('/api/catalog/relationship/{}', productCode),

    public: (): RequestParams =>
      this.request.get().url('/api/public/relationship'),
  };
}
